<template>
  <div class="w-full max-w-xl m-auto tex-center">
    <p class="font-bold mb-4 text-lg">Optional Accessories</p>

    <div v-if="filteredAccessories && filteredAccessories.length > 0" class="grid grid-cols-2 gap-4">
      <div v-for="(accessory, idx) in filteredAccessories" :key="idx"
        class="border border-solid p-4 hover:bg-gray-50 cursor-pointer relative group"
        :class="isAccessorySelected(accessory.id) ? 'border-blue-600 shadow-lg' : 'border-gray-200'"
        @click="toggleAccessory(accessory)">
        <!-- Image section -->
        <div class="flex justify-center mb-3 h-24">
          <img v-if="accessory.img_path" :src="getImageUrl(accessory.img_path)" :alt="accessory.part_number"
            class="max-h-24 object-contain" />
          <div v-else class="bg-gray-200 w-full h-24 flex items-center justify-center">
            <span class="text-gray-400 text-xs">No image</span>
          </div>
        </div>

        <!-- Checkbox in corner - completely hidden -->
        <input type="checkbox" :id="`accessory-${accessory.id}`" :checked="isAccessorySelected(accessory.id)"
          @change="toggleAccessory(accessory)" class="hidden" />

        <!-- Using only border color and shadow to indicate selection, no checkbox -->
        <div v-if="isAccessorySelected(accessory.id)" class="absolute top-3 right-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd" />
          </svg>
        </div>

        <!-- Part number and description -->
        <div class="mb-4 pr-8">
          <div class="font-medium flex items-center gap-2">
            {{ accessory.part_number }}

            <!-- Tooltip icon (optional visual indicator) -->
            <div v-if="accessory.tooltip">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 cursor-help"
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="text-gray-500 text-sm">{{ accessory.description || 'Accessory' }}</div>
        </div>

        <!-- Tooltip content that appears on hover of entire accessory box -->
        <div v-if="accessory.tooltip" class="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-20 pointer-events-none">
          <div class="bg-gray-900 text-white text-xs rounded py-2 px-3 max-w-xs">
            {{ accessory.tooltip }}
            <svg class="absolute text-gray-900 h-2 w-full left-0 top-full" x="0px" y="0px" viewBox="0 0 255 255"
              xml:space="preserve">
              <polygon class="fill-current" points="0,0 127.5,127.5 255,0" />
            </svg>
          </div>
        </div>

        <!-- Quantity section -->
        <div class="flex items-center justify-between mt-4">
          <div class="text-sm text-gray-500">
            <span v-if="hasAutoQuantity(accessory)">Auto-calculated</span>
            <span v-else>Custom quantity</span>
          </div>

          <div
            v-if="!hasAutoQuantity(accessory) && isAccessorySelected(accessory.id)"
            class="flex items-center">
            <button type="button"
              class="border border-gray-300 rounded-l w-8 h-8 bg-gray-100 flex items-center justify-center"
              @click.stop.prevent="decrementQuantity(accessory.id)">
              -
            </button>
            <input type="number" min="1" :value="getAccessoryQuantity(accessory.id)"
              @change.stop.prevent="updateQuantity(accessory.id, $event.target.value)"
              @keydown.enter.prevent="() => { }" class="w-10 h-8 border-t border-b border-gray-300 text-center text-sm"
              @click.stop />
            <button type="button"
              class="border border-gray-300 rounded-r w-8 h-8 bg-gray-100 flex items-center justify-center"
              @click.stop.prevent="incrementQuantity(accessory.id)">
              +
            </button>
          </div>

          <!-- Show auto-calculated quantity -->
          <div v-else-if="hasAutoQuantity(accessory) && isAccessorySelected(accessory.id)"
            class="text-sm font-medium text-gray-700">
            Qty: {{ getAccessoryQuantity(accessory.id) }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center p-10 bg-gray-50 rounded-md">
      <p class="text-gray-600">No accessories available for the selected system.</p>
      <!-- Debug info -->
      <div class="mt-2 text-xs text-gray-400">
        Debug: accessories.length = {{ accessories ? accessories.length : 'null' }},
        filteredAccessories.length = {{ filteredAccessories ? filteredAccessories.length : 'null' }},
        systemId = {{ systemId }}
      </div>
    </div>

    <div id="buttons-4" class="flex items-center justify-between px-8 py-4">
      <a class="btn-blue bg-gray-600" @click.prevent="update('step', value.step - 1)">Previous</a>
      <a class="btn-blue" @click.prevent="update('step', value.step + 1)">Next</a>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: Object,
    accessories: {
      type: Array,
      default: () => []
    },
    systemId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['input'],
  data() {
    return {
      // Empty - all formulas will come from DB
    };
  },
  computed: {
    // Filter out PMCC accessories for GRP system (system_id = 3)
    filteredAccessories() {
      // If it's GRP system (id = 3), filter out PMCC accessories
      if (this.systemId == 3) {
        console.log('before filtering accessories:', this.accessories);
        return this.accessories.filter(acc =>
          !acc.part_number || !acc.part_number.includes('PMCC')
        );
      }
      return this.accessories;
    },
    // Get the selected accessories from the form
    selectedAccessories() {
      return this.value.selectedAccessories || [];
    },

    // Get the number of channels (from the run length)
    channelCount() {
      // Assuming each channel is 1 meter, adjust if needed
      return Math.ceil(parseFloat(this.value.length) || 0);
    },

    // Get total grate length for VP calculations
    totalGrateLength() {
      return parseFloat(this.value.length) || 0;
    }
  },
  methods: {
    // Check if accessory has auto-calculated quantity
    hasAutoQuantity(accessory) {
      // Only use formula from database
      return !!accessory.calculation_formula;
    },

    // Calculate automatic quantity based on formula from DB
    calculateAutoQuantity(accessory) {
      if (!accessory.calculation_formula) {
        return 1; // Default quantity if no formula
      }

      try {
        // Available variables for formulas
        const channels = this.channelCount;
        const length = this.totalGrateLength;
        const channelCount = channels; // alias for backward compatibility
        const grateLength = length; // alias for backward compatibility

        // Evaluate the formula
        // Using Function constructor for better security than eval
        const formula = new Function('channels', 'length', 'channelCount', 'grateLength', 'Math',
          `return ${accessory.calculation_formula}`);

        const result = formula(channels, length, channelCount, grateLength, Math);
        return Math.max(1, Math.round(result)); // Ensure at least 1
      } catch (error) {
        console.error('Error calculating quantity for', accessory.part_number, ':', error);
        return 1; // Default to 1 if formula fails
      }
    },

    // Format image URL properly
    getImageUrl(imgPath) {
      if (!imgPath) return null;

      // Check if it's already a full URL
      if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
        return imgPath;
      }

      // Add the appropriate prefix and query parameters
      if (imgPath.startsWith('/')) {
        // If path starts with a slash, don't add another one
        return `${window.location.origin}${imgPath}?w=120&fit=contain`;
      } else {
        // If it's just a relative path like "accessories/...", add /img/ prefix
        return `${window.location.origin}/img/${imgPath}?w=120&fit=contain`;
      }
    },

    // Check if an accessory is selected
    isAccessorySelected(accessoryId) {
      return this.selectedAccessories.some(item => item.id === accessoryId);
    },

    // Get the quantity of a selected accessory
    getAccessoryQuantity(accessoryId) {
      const accessory = this.selectedAccessories.find(item => item.id === accessoryId);
      return accessory ? accessory.quantity : 1;
    },

    // Increment quantity
    incrementQuantity(accessoryId) {
      const currentSelections = [...this.selectedAccessories];
      const index = currentSelections.findIndex(item => item.id === accessoryId);

      if (index !== -1) {
        currentSelections[index].quantity += 1;
        this.update('form.selectedAccessories', currentSelections);
      }
    },

    // Decrement quantity
    decrementQuantity(accessoryId) {
      const currentSelections = [...this.selectedAccessories];
      const index = currentSelections.findIndex(item => item.id === accessoryId);

      if (index !== -1 && currentSelections[index].quantity > 1) {
        currentSelections[index].quantity -= 1;
        this.update('form.selectedAccessories', currentSelections);
      }
    },

    // Toggle an accessory selection
    toggleAccessory(accessory) {
      let currentSelections = [...this.selectedAccessories];

      if (this.isAccessorySelected(accessory.id)) {
        // Remove accessory
        currentSelections = currentSelections.filter(item => item.id !== accessory.id);
      } else {
        // Add accessory with appropriate quantity
        let quantity = 1;

        if (this.hasAutoQuantity(accessory)) {
          // Use auto-calculated quantity from formula
          quantity = this.calculateAutoQuantity(accessory);
        }

        currentSelections.push({
          id: accessory.id,
          part_id: accessory.id,
          part_type: 'accessory',
          quantity: quantity,
          part_number: accessory.part_number,
          description: accessory.description,
          price: accessory.price,
          img_path: accessory.img_path
        });
      }

      this.update('form.selectedAccessories', currentSelections);
    },

    // Update the quantity of a selected accessory
    updateQuantity(accessoryId, quantity) {
      const currentSelections = [...this.selectedAccessories];
      const index = currentSelections.findIndex(item => item.id === accessoryId);

      if (index !== -1) {
        currentSelections[index].quantity = parseInt(quantity, 10) || 1;
        this.update('form.selectedAccessories', currentSelections);
      }
    },

    // Update the form
    update(key, value) {
      this.$emit('input', { key, value });
    }
  },
  created() {
    // Initialize selectedAccessories if it doesn't exist
    if (!this.value.selectedAccessories) {
      this.update('form.selectedAccessories', []);
    }
  },
  watch: {
    // Update auto-calculated quantities when length changes
    'value.length': function (newLength, oldLength) {
      if (newLength !== oldLength) {
        const currentSelections = [...this.selectedAccessories];
        let updated = false;

        currentSelections.forEach((selection, index) => {
          // Find full accessory object
          const accessory = this.filteredAccessories.find(acc => acc.id === selection.id);
          if (accessory && this.hasAutoQuantity(accessory)) {
            const newQuantity = this.calculateAutoQuantity(accessory);
            if (currentSelections[index].quantity !== newQuantity) {
              currentSelections[index].quantity = newQuantity;
              updated = true;
            }
          }
        });

        if (updated) {
          this.update('form.selectedAccessories', currentSelections);
        }
      }
    }
  }
}
</script>

<style scoped>
/* Add styles for tooltip hover behavior */
.group:hover .group-hover\:block {
  display: block;
}
</style>
